import React, { useState, useEffect } from 'react';
import moment from 'moment';

// Components
import Breadcrumbs from '../../../components/Breadcrumbs';
import SearchAndFilter from '../../../components/Search/SearchAndFilter';
import TeasuryTable from '../../../components/Content/TeasuryTable';
import Pagination from '../../../components/Footer/Pagination';

// Utils
import { formatDate } from '../../../../utils/DateUtils';
import { parseCurrency, getAlert } from '../../../../utils/SamuiUtils';
import { useDataTableKeyboardNavigation, getDataTableRowStyle, getDataTableKeyboardInstructions } from '../../../../utils/DataTableKeyboardNavigation';

const Main = ({ 
  name, 
  dataMasterList = [], 
  dataDetailList = [], 
  statusColours = [], 
  onPageInsert, 
  onPageUpdate,
  getReport 
}) => {
  // State management
  const [filteredDetailList, setFilteredDetailList] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [statusCounts, setStatusCounts] = useState({});
  const [selectedRowIndex, setSelectedRowIndex] = useState(0); // เพิ่ม state สำหรับ keyboard navigation
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(
    localStorage.getItem('itemsPerPageDropdown') || 10
  );
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState(
    localStorage.getItem('searchTerm') || ''
  );
  const [filteredItems, setFilteredItems] = useState([]);
  
  // Date filter states
  const [startDate, setStartDate] = useState(() => {
    const storedStartDate = localStorage.getItem('startDate');
    return storedStartDate || moment().startOf('month').format('YYYY-MM-DD');
  });
  
  const [endDate, setEndDate] = useState(() => {
    const storedEndDate = localStorage.getItem('endDate');
    return storedEndDate || moment().endOf('month').format('YYYY-MM-DD');
  });

  // Initialize data and status counts
  useEffect(() => {
    if (dataMasterList && dataMasterList.length > 0) {
      const counts = {};
      
      dataMasterList.forEach((data) => {
        if (data.WHDocStatus_Name) {
          counts[data.WHDocStatus_Name] = (counts[data.WHDocStatus_Name] || 0) + 1;
        }
      });
      
      setStatusCounts(counts);
      setCurrentPage(1);
      filterItems();
    } else {
      setFilteredItems([]);
    }
  }, [dataMasterList]);

  // Filter when search term or dates change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedRowIndex(0); // รีเซ็ต selectedRowIndex เมื่อกรองข้อมูล
    filterItems();
  }, [searchTerm, startDate, endDate, dataMasterList]);

  // Filter items function
  const filterItems = () => {
    try {
      let filtered = [...dataMasterList];

      // Date filtering
      const start = moment(startDate, 'YYYY-MM-DD');
      const end = moment(endDate, 'YYYY-MM-DD');

      filtered = filtered.filter((item) => {
        const itemDate = moment(item.WHDoc_Date, 'YYYY-MM-DD');
        return itemDate.isBetween(start, end, null, '[]');
      });

      // Search filtering
      if (searchTerm.trim() !== '') {
        const lowercasedSearchTerm = searchTerm.toLowerCase();
        filtered = filtered.filter((item) =>
          (item.WHDoc_No && item.WHDoc_No.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.T_WH_Name && item.T_WH_Name.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.F_WH_Name && item.F_WH_Name.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.Picking_No && item.Picking_No.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.WHDocStatus_Name && item.WHDocStatus_Name.toLowerCase().includes(lowercasedSearchTerm))
        );
      }

      setFilteredItems(filtered);
    } catch (error) {
      getAlert('FAILED', error.message);
      setFilteredItems([]);
    }
  };

  // Handle row selection
  const onRowSelected = (fromData) => {
    // Clear previous data
    setFilteredDetailList([]);
    setSelectedDocument(null);

    setTimeout(() => {
      const filteredDetail = dataDetailList.filter(
        data => parseCurrency(data.WHDoc_Id) === fromData.WHDoc_Id
      );

      if (filteredDetail && filteredDetail.length > 0) {
        setFilteredDetailList(filteredDetail);
      } else {
        setFilteredDetailList([]);
      }

      setSelectedDocument(fromData);
    }, 50);
  };

  // Pagination calculations
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    setSelectedRowIndex(0); // รีเซ็ต selectedRowIndex เมื่อเปลี่ยนหน้า
    localStorage.setItem('currentPage', pageNumber);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

  // เพิ่ม keyboard navigation สำหรับ DataTable
  const fieldMappings = {
    no: 'WHDoc_No' // field ที่ใช้เป็น identifier
  };

  useDataTableKeyboardNavigation(
    true, // isActive
    currentItems, // currentItems
    selectedRowIndex,
    setSelectedRowIndex,
    (whDocNo) => onPageUpdate(whDocNo), // onRowSelected
    fieldMappings,
    currentPage,
    handlePageChange,
    filteredItems.length, // totalItems
    itemsPerPage,
    getReport // getReport function
  );

  // Render status sidebar
  const renderStatusSidebar = () => {
    if (!statusColours.length) return null;

    return (
      <div className="col-1">
        <center>
          <b>สถานะเอกสาร</b>
        </center>
        <br />
        {statusColours.map((statusObj, index) => {
          const { DocSetStaus_Name, DocSetColour } = statusObj;
          const count = statusCounts[DocSetStaus_Name] || 0;

          return (
            <div key={index} className="col-12 mb-3">
              <div
                className="card text-center"
                style={{ backgroundColor: DocSetColour }}
              >
                <div className="card-body d-flex flex-column align-items-center text-white">
                  <div className="row">
                    <div className="col-12">
                      <b style={{ fontSize: '20px', textAlign: 'center' }}>
                        {count}
                      </b>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <b style={{ fontSize: '16px', textAlign: 'center' }}>
                        {DocSetStaus_Name}
                      </b>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Breadcrumbs 
        page={name} 
        items={[
          { name: 'คลังสินค้า', url: '/Warehouse' },
          { name: name, url: '/bill-of-lading' },
        ]} 
      />
      
      <div className="row" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
        {renderStatusSidebar()}
        
        <div className={statusColours.length > 0 ? 'col-11' : 'col-12'}>
          {/* Search and Filter Section */}
          <SearchAndFilter
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchPlaceholder="ค้นหาเลขเอกสาร, รถส่งสินค้า, เลขที่ PK..."
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            showDateFilter={true}
            onAddNew={onPageInsert}
            addButtonText={`สร้าง${name}`}
            showAddButton={true}
          />

          {/* Content Area */}
          <div className="container-fluid card py-3 hidden-scrollbar">
            <div className="row">
              {/* Master Table */}
              <div className="col-6 mb-1" style={{ borderRight: '2px solid #c7c8c9', display: 'flex', flexDirection: 'column', height: '650px' }}>
                <div style={{ flex: '1 1 auto', overflow: 'hidden', position: 'relative' }}>
                  <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
                    <table id="basic-datatables" className="table table-striped table-hover mb-0">
                      <thead className="thead-dark" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#343a40' }}>
                        <tr>
                          <th className="text-center" style={{ width: '15%', minWidth: '80px' }}>วันที่</th>
                          <th className="text-center" style={{ width: '15%', minWidth: '100px' }}>เลขเอกสาร</th>
                          <th className="text-center" style={{ width: '30%', minWidth: '150px' }}>รถส่งสินค้า</th>
                          <th className="text-center" style={{ width: '15%', minWidth: '80px' }}>เลขที่ PK</th>
                          <th className="text-center" style={{ width: '15%', minWidth: '80px' }}>สถานะ</th>
                          <th className="text-center" style={{ width: '10%', minWidth: '50px' }}>
                            <i className="fa fa-edit" />
                          </th>
                        </tr>
                      </thead>
                    <tbody>
                      {currentItems.length > 0 ? (
                        currentItems.map((data, index) => (
                          <tr
                            key={data.WHDoc_Id || index + 1}
                            onClick={() => {
                              setSelectedRowIndex(index);
                              onRowSelected(data);
                            }}
                            style={getDataTableRowStyle(
                              index, 
                              selectedRowIndex, 
                              selectedDocument?.WHDoc_Id, 
                              data, 
                              'WHDoc_Id'
                            )}
                          >
                            <td className="text-center">{formatDate(data.WHDoc_Date)}</td>
                            <td className="text-center">{data.WHDoc_No}</td>
                            <td className="text-left">{data.T_WH_Name}</td>
                            <td className="text-center">{data.Picking_No || '-'}</td>
                            <td className="text-center">
                              <button
                                className="btn"
                                style={{
                                  backgroundColor: data.WHDocStatus_Colour,
                                  color: 'white',
                                  width: '100%'
                                }}
                              >
                                {data.WHDocStatus_Name}
                              </button>
                            </td>
                            <td className="text-center">
                              <button 
                                className="btn btn-sm" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onPageUpdate(data.WHDoc_No);
                                }}
                              >
                                <i className="fa fa-edit" />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6">
                            <center>
                              <h5>ไม่พบข้อมูล</h5>
                            </center>
                          </td>
                        </tr>
                      )}
                    </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination & Items per page selector fixed at bottom of table */}
                <div className="bg-white border-top" style={{ flex: '0 0 auto', padding: '8px 0' }}>
                  <div className="row m-0">
                    <div className="col-12 d-flex justify-content-between align-items-center flex-wrap py-2">
                      <div className="d-flex align-items-center flex-wrap">
                        <span className="me-2">แสดง:</span>
                        <select
                          className="form-select me-2"
                          style={{ width: 'auto', minWidth: '70px' }}
                          value={itemsPerPage}
                          onChange={(e) => {
                            const newItemsPerPage = parseInt(e.target.value);
                            setItemsPerPage(newItemsPerPage);
                            localStorage.setItem('itemsPerPageDropdown', newItemsPerPage);
                            setCurrentPage(1);
                          }}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                        <span>รายการต่อหน้า | แสดง {indexOfFirstItem} ถึง {Math.min(indexOfLastItem, filteredItems.length)} จาก {filteredItems.length} รายการ</span>
                      </div>
                      <div className="d-flex align-items-center flex-wrap">
                        <div className="text-muted me-3" style={{ fontSize: "12px" }}>
                          {getDataTableKeyboardInstructions(!!getReport, true)}
                        </div>
                        <Pagination
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                          itemsPerPage={itemsPerPage}
                          dataMasterList={filteredItems}
                          handlePageChange={handlePageChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detail Panel */}
              <div className="col-6 mb-3">
                <div className="d-flex align-items-center bg-light p-3 rounded shadow-sm position-relative">
                  <div className="flex-grow-1 text-center position-relative">
                    <p className="card-title mb-0 fw-bold">
                      เอกสารเลขที่: <span className="text-primary fw-bold fs-4">
                        {selectedDocument?.WHDoc_No || '-'}
                      </span>
                    </p>
                    <p className="mb-0 small">
                      เบิกสินค้าจากคลัง: <span className="text-info fw-bold fs-5">
                        {selectedDocument?.F_WH_Name || '-'}
                      </span>
                      <span className="mx-2">ไปยังรถ:</span>
                      <span className="text-success fw-bold fs-5">
                        {selectedDocument?.T_WH_Name || '-'}
                      </span>
                    </p>
                  </div>
                </div>
                <TeasuryTable dataList={filteredDetailList} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Main;
